import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix"
import { json } from "@remix-run/node"
import type {
  MetaFunction,
  LinksFunction,
  LoaderFunctionArgs,
  HeadersFunction
} from "@remix-run/node"
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  isRouteErrorResponse,
  useLoaderData,
  useLocation,
  useRouteError
} from "@remix-run/react"
import ApplyDiscount from "~/components/ApplyDiscount"
import Loading from "~/components/Loading"
import { buildMetaTags } from "~/helpers/meta-tags"
import { AnalyticsProvider } from "~/providers/AnalyticsProvider"
import { BackendApiProvider } from "~/providers/BackendApiProvider"
import { ConfigProvider, getPublicConfig } from "~/providers/ConfigProvider"
import { ContentfulClientProvider } from "~/providers/ContentfulClientProvider"
import GrowthBookProvider from "~/providers/GrowthBookProvider"
import { NotificationsProvider } from "~/providers/NotificationsProvider"
import { ShopifyCartProvider } from "~/providers/ShopifyCartProvider"
import { ShopifyClientProvider } from "~/providers/ShopifyClientProvider"
import { isAuthorized } from "~/services/auth.server"
import { useEffect, useState } from "react"
import { cssBundleHref } from "@remix-run/css-bundle"
import stylesUrl from "~/styles/index.css?url"
import responsiveTabletUrl from "~/styles/responsive/tablet.css?url"
import responsiveDesktopUrl from "~/styles/responsive/desktop.css?url"
import responsivePrintUrl from "~/styles/responsive/print.css?url"

export const loader = ({ request }: LoaderFunctionArgs) => {
  if (!isAuthorized(request)) {
    throw json("Not authorized.", { status: 401 })
  }

  return json({
    config: getPublicConfig(request)
  })
}

export const links: LinksFunction = () => [
  // Core styles (includes mobile-first responsive design)
  { rel: "stylesheet", href: stylesUrl },

  // CSS Bundle for shared styles
  ...(cssBundleHref ? [{ rel: "stylesheet", href: cssBundleHref }] : []),

  // Responsive stylesheets with media queries
  {
    rel: "stylesheet",
    href: responsiveTabletUrl,
    media: "screen and (min-width: 768px)"
  },
  {
    rel: "stylesheet",
    href: responsiveDesktopUrl,
    media: "screen and (min-width: 1024px)"
  },
  {
    rel: "stylesheet",
    href: responsivePrintUrl,
    media: "print"
  },

  { rel: "icon", href: "/favicon.png" },
  // Only preconnect to the most critical origins needed for initial page load
  { rel: "preconnect", href: "https://images.ctfassets.net" }, // Critical for images
  { rel: "preconnect", href: "https://cached-api-production.camp.com" }, // Critical for API data
  // DNS prefetch for less critical origins (cheaper than preconnect)
  { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
  { rel: "dns-prefetch", href: "https://stream.mux.com" },
  { rel: "dns-prefetch", href: "https://connect.facebook.net" },
  { rel: "dns-prefetch", href: "https://analytics.tiktok.com" },
  {
    rel: "preload",
    href: "/fonts/GeneralSans-Medium-subset.woff2",
    as: "font",
    type: "font/woff2",
    crossOrigin: "anonymous",
    fetchPriority: "high"
  },
  {
    rel: "preload",
    href: "/fonts/GeneralSans-Semibold-subset.woff2",
    as: "font",
    type: "font/woff2",
    crossOrigin: "anonymous",
    fetchPriority: "high"
  },
  {
    rel: "preload",
    href: "/fonts/Youth-Bold-subset.woff2",
    as: "font",
    type: "font/woff2",
    crossOrigin: "anonymous",
    fetchPriority: "high"
  }
]

export const headers: HeadersFunction = () => ({
  "WWW-Authenticate": "Basic",
  // HTTP/2 Server Push for critical resources
  Link: [
    `</fonts/GeneralSans-Medium-subset.woff2>; rel=preload; as=font; type="font/woff2"; crossorigin=anonymous`,
    `</fonts/GeneralSans-Semibold-subset.woff2>; rel=preload; as=font; type="font/woff2"; crossorigin=anonymous`,
    `</fonts/Youth-Bold-subset.woff2>; rel=preload; as=font; type="font/woff2"; crossorigin=anonymous; fetchpriority=high`,
    // Preconnect to critical origins via header
    `<https://images.ctfassets.net>; rel=preconnect`
  ].join(", ")
})

export const meta: MetaFunction = ({ matches, location }) =>
  buildMetaTags(matches, location)

function App() {
  const { config } = useLoaderData<typeof loader>()

  const [lastLocation, setLastLocation] = useState("")
  let location = useLocation()

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual"
    }
  }, [])

  useEffect(() => {
    if (lastLocation == location.pathname) {
      return
    }
    setLastLocation(location.pathname)

    window.scrollTo({ top: 0, left: 0 })

    const history = JSON.parse(
      window.localStorage.getItem("journeyHistory") || "[]"
    )
    history.push({
      time: new Date().toISOString(),
      pathname: location.pathname,
      location: window.location.href
    })
    window.localStorage.setItem(
      "journeyHistory",
      JSON.stringify(history.splice(-100))
    )
  }, [location, lastLocation])

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Meta />
        <Links />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify({ sentryDsn: config.sentryDsn, sentryEnvironment: config.sentryEnvironment, sentryTracesSampleRate: config.sentryTracesSampleRate, sentryReplaysSampleRate: config.sentryReplaysSampleRate })};`
          }}
        />
      </head>
      <body className="bg-ambient-gray text-camp-primary leading-tight subpixel-antialiased">
        <ConfigProvider values={config}>
          <Loading />

          <BackendApiProvider>
            <ShopifyClientProvider>
              <ContentfulClientProvider>
                <AnalyticsProvider>
                  <GrowthBookProvider>
                    <NotificationsProvider>
                      <ShopifyCartProvider>
                        <ApplyDiscount />
                        <Outlet />
                      </ShopifyCartProvider>
                    </NotificationsProvider>
                  </GrowthBookProvider>
                </AnalyticsProvider>
              </ContentfulClientProvider>
            </ShopifyClientProvider>
          </BackendApiProvider>
        </ConfigProvider>
        <Scripts />
      </body>
    </html>
  )
}

export const ErrorBoundary = () => {
  const error = useRouteError()
  captureRemixErrorBoundaryError(error)

  const isDev = process.env.NODE_ENV === "development"
  const message = isRouteErrorResponse(error)
    ? `${error.status}: ${error.data || "Unknown error"}`
    : error instanceof Error
      ? error.message
      : "An unexpected error occurred"

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <title>Something went wrong</title>
        <Links />
      </head>
      <body className="bg-ambient-gray text-camp-primary flex min-h-screen items-center justify-center">
        <div className="text-center px-4">
          <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
          <p className="text-gray-600 mb-4">
            Please try refreshing the page. If the problem persists, contact
            support.
          </p>
          {isDev && (
            <pre className="mt-4 text-left text-sm bg-gray-100 p-4 rounded max-w-lg mx-auto overflow-auto">
              {message}
            </pre>
          )}
        </div>
        <Scripts />
      </body>
    </html>
  )
}

export default withSentry(App)
